import React, { useState } from 'react';
import { Download, FileText, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import html2canvas from 'html2canvas';

/**
 * PDFExportButton - Export dashboard content to PDF
 * Supports multiple export modes: screenshot, data table, or full report
 */
export default function PDFExportButton({ 
  title = 'Report',
  subtitle = '',
  data = null,
  columns = null,
  targetRef = null, // Ref to element to capture as screenshot
  fileName = 'report',
  size = 'default', // 'sm' | 'default' | 'lg'
  variant = 'outline'
}) {
  const [exporting, setExporting] = useState(false);

  const exportAsScreenshot = async () => {
    if (!targetRef?.current) {
      toast.error('No content to export');
      return;
    }

    setExporting(true);
    try {
      const canvas = await html2canvas(targetRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
        unit: 'px',
        format: [canvas.width, canvas.height]
      });

      pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
      pdf.save(`${fileName}_${new Date().toISOString().split('T')[0]}.pdf`);
      
      toast.success('PDF exported successfully');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  const exportAsDataTable = async () => {
    if (!data || !columns) {
      toast.error('No data to export');
      return;
    }

    setExporting(true);
    try {
      const pdf = new jsPDF();
      
      // Add title
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.text(title, 14, 22);
      
      if (subtitle) {
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(100);
        pdf.text(subtitle, 14, 30);
      }
      
      // Add date
      pdf.setFontSize(10);
      pdf.setTextColor(150);
      pdf.text(`Generated: ${new Date().toLocaleString()}`, 14, subtitle ? 38 : 30);

      // Add table
      const tableData = data.map(row => 
        columns.map(col => row[col.key] ?? '')
      );

      pdf.autoTable({
        startY: subtitle ? 45 : 35,
        head: [columns.map(col => col.label)],
        body: tableData,
        theme: 'striped',
        styles: { 
          fontSize: 9,
          cellPadding: 3
        },
        headStyles: {
          fillColor: [79, 70, 229], // violet-600
          textColor: 255,
          fontStyle: 'bold'
        },
        alternateRowStyles: {
          fillColor: [245, 245, 255]
        }
      });

      pdf.save(`${fileName}_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('PDF exported successfully');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  const exportAsReport = async () => {
    setExporting(true);
    try {
      const pdf = new jsPDF();
      
      // Header with gradient-like effect
      pdf.setFillColor(79, 70, 229);
      pdf.rect(0, 0, 220, 45, 'F');
      
      // Title
      pdf.setFontSize(22);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(255);
      pdf.text(title, 14, 25);
      
      if (subtitle) {
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'normal');
        pdf.text(subtitle, 14, 35);
      }
      
      // Reset text color
      pdf.setTextColor(0);
      
      // Add date and footer info
      pdf.setFontSize(9);
      pdf.setTextColor(100);
      pdf.text(`Generated: ${new Date().toLocaleString()}`, 14, 55);
      pdf.text('Hospice CareLens - Quality Intelligence Platform', 14, 62);
      
      // Add data sections
      if (data && Array.isArray(data)) {
        let yPos = 75;
        
        data.forEach((section, idx) => {
          if (yPos > 250) {
            pdf.addPage();
            yPos = 20;
          }
          
          if (section.title) {
            pdf.setFontSize(14);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(79, 70, 229);
            pdf.text(section.title, 14, yPos);
            yPos += 8;
          }
          
          if (section.content) {
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(60);
            const lines = pdf.splitTextToSize(section.content, 180);
            pdf.text(lines, 14, yPos);
            yPos += lines.length * 5 + 10;
          }
          
          if (section.metrics && Array.isArray(section.metrics)) {
            section.metrics.forEach(metric => {
              pdf.setFontSize(10);
              pdf.setFont('helvetica', 'bold');
              pdf.setTextColor(0);
              pdf.text(`${metric.label}: `, 14, yPos);
              pdf.setFont('helvetica', 'normal');
              pdf.text(String(metric.value), 14 + pdf.getTextWidth(`${metric.label}: `), yPos);
              yPos += 6;
            });
            yPos += 5;
          }
        });
      }
      
      // Footer on last page
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(150);
        pdf.text(
          `Page ${i} of ${pageCount}`,
          pdf.internal.pageSize.width / 2,
          pdf.internal.pageSize.height - 10,
          { align: 'center' }
        );
      }

      pdf.save(`${fileName}_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('PDF exported successfully');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  // Simple button if only one export option makes sense
  if (targetRef) {
    return (
      <Button
        variant={variant}
        size={size}
        onClick={exportAsScreenshot}
        disabled={exporting}
        className="gap-2"
        data-testid="pdf-export-btn"
      >
        {exporting ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Download className="h-4 w-4" />
        )}
        Export PDF
      </Button>
    );
  }

  // Dropdown for multiple options
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant}
          size={size}
          disabled={exporting}
          className="gap-2"
          data-testid="pdf-export-btn"
        >
          {exporting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
          Export PDF
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {data && columns && (
          <DropdownMenuItem onClick={exportAsDataTable}>
            <FileText className="h-4 w-4 mr-2" />
            Export Data Table
          </DropdownMenuItem>
        )}
        {data && !columns && (
          <DropdownMenuItem onClick={exportAsReport}>
            <FileText className="h-4 w-4 mr-2" />
            Export Full Report
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
